import { getStoredToken, notifyUnauthorized } from "./session";

export type ApiError = { error: string };

async function request<T>(input: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getStoredToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");

  const resp = await fetch(input, { ...init, headers });

  if (!resp.ok) {
    // 401 = token absent, expiré ou invalide : on purge la session pour que le garde de
    // route renvoie sur la connexion. Le login est exclu, son 401 signifie "mauvais
    // identifiants" et non "session expirée". Le 403 n'est PAS concerné : c'est l'écran
    // "accès refusé" (droit WsdAdmin manquant), l'utilisateur reste connecté.
    if (resp.status === 401 && !input.startsWith("/api/auth/login"))
      notifyUnauthorized();

    let message = `${resp.status} ${resp.statusText}`;
    try {
      const body = (await resp.json()) as Partial<ApiError>;
      if (body?.error) message = body.error;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }

  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

// --- Backend WsdAdmin (proxifié sur /api en dev)
export const api = {
  me: () => request<Me>("/api/me"),
  accounts: {
    list: () => request<Account[]>("/api/accounts"),
    create: (body: CreateAccountRequest) =>
      request<AccountPasswordResult>("/api/accounts", { method: "POST", body: JSON.stringify(body) }),
    resetPassword: (idUserAPI: number) =>
      request<AccountPasswordResult>(`/api/accounts/${idUserAPI}/password`, { method: "POST" }),
    setProfile: (idUserAPI: number, profile: string | null) =>
      request<void>(`/api/accounts/${idUserAPI}/profile`, { method: "POST", body: JSON.stringify({ profile }) }),
    update: (idUserAPI: number, body: UpdateAccountRequest) =>
      request<void>(`/api/accounts/${idUserAPI}`, { method: "PUT", body: JSON.stringify(body) }),
  },
  referentials: {
    /** Recherche de sociétés dans le référentiel maître (relayée à Milo). */
    companies: (search: string) =>
      request<CompanyRef[]>(`/api/referentials/companies?search=${encodeURIComponent(search)}`),
  },
  rights: {
    get: (idUserAPI: number) => request<AccountRights>(`/api/accounts/${idUserAPI}/rights`),
    locatsReferential: () => request<LocatRef[]>("/api/referentials/locats"),
    marketsReferential: () => request<MarketRef[]>("/api/referentials/markets"),
    addLocat: (idUserAPI: number, body: AddLocatRightRequest) =>
      request<void>(`/api/accounts/${idUserAPI}/locats`, { method: "POST", body: JSON.stringify(body) }),
    removeLocat: (idUserAPILocat: number) =>
      request<void>(`/api/locats/${idUserAPILocat}`, { method: "DELETE" }),
    addMarket: (idUserAPILocat: number, body: AddMarketRightRequest) =>
      request<void>(`/api/locats/${idUserAPILocat}/markets`, { method: "POST", body: JSON.stringify(body) }),
    removeMarket: (idUserAPILocat: number, idMarkets: number) =>
      request<void>(`/api/locats/${idUserAPILocat}/markets/${idMarkets}`, { method: "DELETE" }),
    addOption: (idUserAPILocat: number, body: AddOptionRightRequest) =>
      request<void>(`/api/locats/${idUserAPILocat}/options`, { method: "POST", body: JSON.stringify(body) }),
    removeOption: (idUserAPILocat: number, idMarket: number) =>
      request<void>(`/api/locats/${idUserAPILocat}/options/${idMarket}`, { method: "DELETE" }),
  },
};

// --- Auth (proxifiée vers Milo via le backend WsdAdmin, pour éviter le CORS)
export const miloAuth = {
  login: async (login: string, password: string) => {
    const data = await request<{ token: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ Login: login, Password: password }),
    });
    return data.token;
  },
};

// --- Types alignés avec le backend
export type Me = { userId: number; isAdmin: boolean };

export type Account = {
  idUserAPI: number;
  login: string;
  nom: string;
  idEntreprise: number;
  /** Lien local dbo.Companies.CompanyId, null si le compte n'est pas rattaché. */
  idCompany: number | null;
  nomEntreprise: string | null;
  /** Id de la société dans le référentiel maître : valeur attendue par le formulaire. */
  actilogCompanyId: number | null;
  hasV2Access: boolean;
  /** Profil d'accès aux endpoints réservés de WsdApiV2 : null = standard, "agrimarket". */
  profile: string | null;
};

// Profils d'accès connus de WsdApiV2 (valeur "" = standard, pour les <select>).
export const PROFILE_OPTIONS = [
  { value: "", label: "Standard" },
  { value: "agrimarket", label: "Agrimarket" },
];

/**
 * Création d'un compte. `idEntreprise` est l'id du référentiel MAÎTRE (DB_CRACQ) et
 * `nomEntreprise` accompagne l'appel car le serveur en a besoin pour créer la ligne locale
 * dbo.Companies si la société n'y figure pas encore.
 */
export type CreateAccountRequest = {
  login: string;
  nom: string;
  idEntreprise: number;
  nomEntreprise: string;
};

/** Modification d'un compte. Le login est modifiable, au prix d'une coupure d'accès pour le client. */
export type UpdateAccountRequest = {
  login: string;
  nom: string;
  idEntreprise: number;
  nomEntreprise: string;
};

/** Société du référentiel maître (DB_CRACQ), renvoyée par la recherche via Milo. */
export type CompanyRef = { idEntreprise: number; nomEntreprise: string };

// Réponse de création / régénération : la seule fois où le mot de passe transite en clair.
export type AccountPasswordResult = { idUserAPI: number; login: string; password: string };

// --- Droits
// Mode UserAPI_Locat : 1 = historique, 2 = différé, 3 = temps réel.
export const MODE_LABELS: Record<number, string> = { 1: "Historique", 2: "Différé", 3: "Temps réel" };

export type MarketRight = { idMarkets: number; labelledMarkets: string; numExpiries: number[] };
export type MarketOptionRight = { idMarket: number; labelledMarkets: string };

// Type de place (Locat.type) : "C" = cash (géré au niveau place, sans marché ni option), sinon future.
export type LocatRight = {
  idUserAPILocat: number;
  locat: number;
  nomLocat: string;
  mode: number;
  interdireCarnetOrdre: boolean;
  type: string;
  markets: MarketRight[];
  options: MarketOptionRight[];
};

export type AccountRights = {
  idUserAPI: number;
  login: string;
  locats: LocatRight[];
};

export type LocatRef = { locat: number; nomLocat: string; type: string };
export type MarketRef = { idMarkets: number; labelledMarkets: string; locat: number };

export type AddLocatRightRequest = { locat: number; mode: number; interdireCarnetOrdre: boolean };
// numExpiry : 0 = toutes les échéances (cas général), N = échéance précise.
export type AddMarketRightRequest = { idMarkets: number; numExpiry: number };
export type AddOptionRightRequest = { idMarket: number };
